// The /conteudos archive. Prerendered on the server, then hydrated so it can
// reveal scheduled posts on their day without a rebuild (see the `now` state
// below). Layout follows the "Single Candle": the newest *due* post is the one
// lit element (the only accent on the screen); everything older sits in a quiet
// chronological list separated by tone and hairlines, never cards.
import { useEffect, useState } from "react";
import all from "../generated/all.json";
import type { ContentLight } from "../content/types";
import { SiteHeader } from "./SiteHeader";
import { Footer } from "./Footer";
import { todayInSaoPaulo } from "../lib/schedule";

// Newest-first, and it includes posts scheduled for a future `data` — those are
// filtered out below until their day arrives.
const items = all as ContentLight[];

export function ContentIndex({ buildNow }: { buildNow: string }) {
  // The first client render must match the server, so it starts on `buildNow`
  // (the date baked at build time). Right after mount we swap to the visitor's
  // real Brazil date: any post whose day has arrived since the last deploy
  // reveals itself — and the newest one promotes itself to the candle — with no
  // rebuild. Without JS the page simply shows whatever was due at build time.
  const [now, setNow] = useState(buildNow);
  useEffect(() => {
    setNow(todayInSaoPaulo());
  }, []);

  const due = items.filter((it) => it.data <= now);
  const [candle, ...rest] = due;

  return (
    <>
      <SiteHeader />
      <main className="cx">
        <header className="cx__masthead">
          <h1 className="cx__title">Reflexões</h1>
          <p className="cx__intro">
            Reflexões curtas de fé — as mesmas que chegam todo dia no Telegram. Sem pressa,
            sem tese. Leia a que te encontrar hoje.
          </p>
        </header>

        {candle && (
          <a className="cx__candle" href={candle.url}>
            <span className="cx__candleGlow" aria-hidden="true" />
            <div className="cx__candleBody">
              <span className="cx__candleTag">Mais recente</span>
              <time className="cx__candleDate" dateTime={candle.data}>
                {candle.dataDisplay}
              </time>
              <h2 className="cx__candleTitle">{candle.titulo}</h2>
              {candle.versiculo && <p className="cx__candleVerse">{candle.versiculo}</p>}
              <p className="cx__candleResumo">{candle.resumo}</p>
              <span className="cx__candleCta" aria-hidden="true">
                Ler agora →
              </span>
            </div>
            {candle.imagem && (
              <div className="cx__candleMedia">
                <img src={candle.imagem} alt={candle.titulo} width={1024} height={576} />
              </div>
            )}
          </a>
        )}

        {rest.length > 0 && (
          <ul className="cx__list">
            {rest.map((it) => (
              <li key={it.slug} className="cx__row">
                <a className="cx__rowLink" href={it.url}>
                  <time className="cx__rowDate" dateTime={it.data}>
                    {it.dataDisplay}
                  </time>
                  <span className="cx__rowText">
                    <h3 className="cx__rowTitle">{it.titulo}</h3>
                    <p className="cx__rowResumo">{it.resumo}</p>
                  </span>
                  {it.imagem && (
                    <img
                      className="cx__rowThumb"
                      src={it.imagem}
                      alt={it.titulo}
                      width={320}
                      height={180}
                      loading="lazy"
                    />
                  )}
                </a>
              </li>
            ))}
          </ul>
        )}

        {due.length === 0 && (
          <p className="cx__empty">As primeiras reflexões chegam em breve.</p>
        )}
      </main>
      <Footer />
    </>
  );
}
